import { useEffect, useCallback, useState } from 'react';

// Type definitions for Umami global object
interface UmamiEventData {
  [key: string]: string | number | boolean;
}

interface UmamiTracker {
  track: (eventName: string, eventData?: UmamiEventData) => void;
  identify: (userData: {
    userId?: string;
    email?: string;
    [key: string]: unknown;
  }) => void;
}

// Extend Window interface
declare global {
  interface Window {
    umami?: UmamiTracker;
  }
}

/**
 * Check if user has given consent for analytics cookies
 *
 * Reads from localStorage key 'trackv16-cookie-consent'
 * Returns true only if user explicitly accepted analytics cookies
 */
function hasAnalyticsConsent(): boolean {
  try {
    const consentStr = localStorage.getItem('trackv16-cookie-consent');
    if (!consentStr) return false;

    const consent = JSON.parse(consentStr);
    return consent.analytics === true;
  } catch (error) {
    console.error('[Analytics] Error reading consent:', error);
    return false;
  }
}

/**
 * Load Umami script dynamically (only if consent given)
 */
let scriptLoaded = false;
let scriptLoading = false;

function loadUmamiScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check consent BEFORE loading script
    if (!hasAnalyticsConsent()) {
      if (import.meta.env.DEV) {
        console.log(
          '[Analytics] Umami script NOT loaded (no analytics consent)'
        );
      }
      resolve();
      return;
    }

    // If already loaded, resolve immediately
    if (scriptLoaded && window.umami) {
      resolve();
      return;
    }

    // If already loading, wait for it
    if (scriptLoading) {
      const checkLoaded = setInterval(() => {
        if (scriptLoaded && window.umami) {
          clearInterval(checkLoaded);
          resolve();
        }
      }, 100);
      return;
    }

    const websiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID;
    const scriptSrc = import.meta.env.VITE_UMAMI_SRC;

    // Check if we have the required config
    if (!websiteId || !scriptSrc) {
      console.warn(
        '[Analytics] Umami not configured. Set VITE_UMAMI_WEBSITE_ID and VITE_UMAMI_SRC'
      );
      resolve();
      return;
    }

    scriptLoading = true;

    // Create script element
    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-website-id', websiteId);

    // Only restrict domains in production
    if (import.meta.env.PROD) {
      script.setAttribute('data-domains', 'trackv16.com,www.trackv16.com');
    }
    // In development, allow all domains (including localhost)

    script.onload = () => {
      scriptLoaded = true;
      scriptLoading = false;
      if (import.meta.env.DEV) {
        console.log('[Analytics] Umami script loaded successfully');
      }
      resolve();
    };

    script.onerror = () => {
      scriptLoading = false;
      console.error('[Analytics] Failed to load Umami script');
      reject(new Error('Failed to load Umami script'));
    };

    document.head.appendChild(script);
  });
}

/**
 * Custom hook for Umami analytics
 * Provides methods to track events, page views, and identify users
 */
export function useAnalytics() {
  const [isEnabled, setIsEnabled] = useState(false);

  // Load Umami script on mount
  useEffect(() => {
    const initUmami = async () => {
      try {
        await loadUmamiScript();
        // Check if window.umami is available
        if (window.umami) {
          setIsEnabled(true);
        }
      } catch (error) {
        console.error('[Analytics] Failed to initialize Umami:', error);
      }
    };

    initUmami();
  }, []);

  /**
   * Track a custom event
   * @param eventName - Name of the event (e.g., 'button_click', 'form_submit')
   * @param eventData - Optional data associated with the event
   */
  const trackEvent = useCallback(
    (eventName: string, eventData?: UmamiEventData) => {
      if (!isEnabled || !window.umami) {
        if (import.meta.env.DEV) {
          console.log('[Analytics] Event:', eventName, eventData);
        }
        return;
      }

      try {
        window.umami.track(eventName, eventData);
      } catch (error) {
        console.error('[Analytics] Track event error:', error);
      }
    },
    [isEnabled]
  );

  /**
   * Track a page view manually (automatic with Umami script, but useful for SPAs)
   * @param path - Optional custom path to track
   */
  const trackPageView = useCallback(
    (path?: string) => {
      if (!isEnabled || !window.umami) return;

      try {
        const url = path || window.location.pathname + window.location.search;
        window.umami.track('pageview', { url });
      } catch (error) {
        console.error('[Analytics] Track page view error:', error);
      }
    },
    [isEnabled]
  );

  /**
   * Identify a user (optional, only if user has consented)
   * @param userId - Unique user identifier
   * @param email - User email (optional)
   * @param additionalData - Any additional user properties
   */
  const identifyUser = useCallback(
    (
      userId: string,
      email?: string,
      additionalData?: Record<string, unknown>
    ) => {
      if (!isEnabled || !window.umami) return;

      // Check consent
      const hasConsent = localStorage.getItem('analytics-consent');
      if (hasConsent === 'false') return;

      try {
        window.umami.identify({
          userId,
          email,
          ...additionalData,
        });
      } catch (error) {
        console.error('[Analytics] Identify user error:', error);
      }
    },
    [isEnabled]
  );

  /**
   * Clear user identification (call on logout)
   */
  const clearUser = useCallback(() => {
    if (!isEnabled || !window.umami) return;

    try {
      window.umami.identify({});
    } catch (error) {
      console.error('[Analytics] Clear user error:', error);
    }
  }, [isEnabled]);

  return {
    isEnabled,
    trackEvent,
    trackPageView,
    identifyUser,
    clearUser,
  };
}

/**
 * Hook to automatically track page views on route changes
 * Use this in your App component
 */
export function usePageTracking() {
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    // Track initial page view
    trackPageView();

    // React Router handles navigation, but we'll listen to popstate for browser back/forward
    const handlePopState = () => {
      trackPageView();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [trackPageView]);
}
